import React from 'react';
import {
  HandymanOutlined as HandymanIcon,
  YardOutlined as YardIcon,
  SchoolOutlined as SchoolIcon,
  SpaOutlined as SpaIcon,
  PetsOutlined as PetsIcon,
  LocalShippingOutlined as ShippingIcon,
  StorefrontOutlined as StorefrontIcon,
} from '@mui/icons-material';

/**
 * A colour and a picture per top-level category.
 *
 * A directory of small businesses has almost no logos, so every card used to
 * look the same and the eye had nothing to land on before reading. The
 * vignette is that landing place: same trade, same colour, every time.
 *
 * Keyed on the ROOT category — `parent_slug` when the listing sits under a
 * sub-category, its own slug when it is filed at the top level — because six
 * colours can be told apart and twenty-seven cannot. The slugs are the ones
 * the directory ships with; anything an administrator adds later falls back
 * to the neutral shop front rather than to a wrong picture.
 */
const CATEGORY_LOOKS = {
  'travaux-et-reparations': { tint: '#eef1e6', ink: '#4a5d3a', icon: <HandymanIcon /> },
  'maison-et-jardin': { tint: '#e9f0e4', ink: '#43663f', icon: <YardIcon /> },
  'enfance-et-education': { tint: '#e6edf2', ink: '#2e4a5f', icon: <SchoolIcon /> },
  'bien-etre-et-sante': { tint: '#eae6ef', ink: '#5d4a6b', icon: <SpaIcon /> },
  animaux: { tint: '#fdf3e0', ink: '#7a4a06', icon: <PetsIcon /> },
  'services-du-quotidien': { tint: '#f7ecdc', ink: '#a84a26', icon: <ShippingIcon /> },
};

const NEUTRAL_LOOK = { tint: '#f0ece0', ink: '#6b6154', icon: <StorefrontIcon /> };

/** @param {{slug: string, parent_slug: ?string}} category */
export function categoryLook(category) {
  if (!category) {
    return NEUTRAL_LOOK;
  }

  return CATEGORY_LOOKS[category.parent_slug || category.slug] || NEUTRAL_LOOK;
}

/**
 * What a listing is and where it is, in that order: "Boulangerie ·
 * Centre-ville".
 *
 * One line, one order, everywhere it appears. The trade comes first because it
 * is what somebody is looking for — the neighbourhood narrows a list of
 * bakeries, nobody browses a neighbourhood hoping for a trade — and because
 * the cards are read down a column, where a first word that changes meaning
 * from one card to the next has to be read twice.
 *
 * Takes either the objects the directory returns (`{name}`) or the plain
 * names an offer carries, since the two payloads shape them differently and
 * the reader does not care which.
 */
export function listingMeta(category, neighborhood) {
  const name = (value) => (value && 'object' === typeof value ? value.name : value) || '';

  return [name(category), name(neighborhood)].filter(Boolean).join(' · ');
}
